import { useContext, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { LoadingSpinner } from '../components/BaseComponents'
import usePublicApi from '../hooks/usePublicApi'
import { userContext } from '../context/UserContext'
import { TEXT_LINK_FOCUS_RING } from '../lib/a11yFocus'
import { getCSRFToken } from '../lib/utils'
import routes from '../components/Router/routes'

type Status = 'loading' | 'failed'

// B-6b (D11): the emailed link lands here; the token is POSTed so link scanners cannot consume it.
function VerifyEmail() {
	const publicApi = usePublicApi()
	const { userData, fetchUserData } = useContext(userContext)
	const [qs] = useSearchParams()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	// Captured once so the token can be removed from the address bar without losing it.
	const [token] = useState(() => qs.get('token'))
	const [status, setStatus] = useState<Status>(token ? 'loading' : 'failed')

	useEffect(() => {
		if (!token) {
			return
		}
		// Keep the secret out of browser history and any later Referer.
		navigate(pathname, { replace: true })
		let cancelled = false
		const run = async () => {
			try {
				await publicApi.post('/verifyEmail', { token, _csrf: await getCSRFToken() })
				if (cancelled) {
					return
				}
				if (userData) {
					// Refresh the session so the unverified banner clears if they are already signed in.
					await fetchUserData()
					navigate(routes.landingPage.path, { replace: true })
					return
				}
				navigate(routes.publicLogin.path, {
					replace: true,
					state: { message: 'Your email address has been verified, please log in below.' },
				})
			} catch (_) {
				if (!cancelled) {
					setStatus('failed')
				}
			}
		}
		run()
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token])

	if (status === 'loading') {
		return <LoadingSpinner />
	}

	return (
		<div className='content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 text-center'>
			<div className='text-charcoal-100 text-2xl'>Verification link problem</div>
			<div className='text-charcoal-100 mt-4'>
				This link has already been used, is invalid, or has expired.
			</div>
			<div className='mt-6 text-charcoal-100 space-y-2'>
				<div>
					{userData ? (
						<Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.landingPage.path}>
							Continue
						</Link>
					) : (
						<>
							Already verified your email?{' '}
							<Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.publicLogin.path}>
								Log in
							</Link>
						</>
					)}
				</div>
				{!userData && (
					<div>
						Don't yet have an account?{' '}
						<Link className={`underline text-algae-500 ${TEXT_LINK_FOCUS_RING}`} to={routes.publicSignUp.path}>
							Sign up
						</Link>
					</div>
				)}
			</div>
		</div>
	)
}

export default VerifyEmail
